import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { 
  ArrowLeft, FileText, Truck, MapPin, 
  Calendar, Hash, Box, DollarSign, Calculator
} from 'lucide-react';
import toast from 'react-hot-toast';

const ComprasDetalle = ({ id, onNavigate }) => {
  const [compra, setCompra] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) fetchDetalle();
  }, [id]);

  const fetchDetalle = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('purchases')
        .select(`*, proveedores(*), sitios(*), purchase_items(*, products(*))`)
        .eq('id', id)
        .single();

      if (error) throw error;
      setCompra(data);
      setItems(data.purchase_items || []);
    } catch (error) {
      toast.error("Error al cargar detalle");
      onNavigate('compras');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-10 text-center font-black text-slate-400 uppercase text-[10px] animate-pulse">Auditando operación...</div>;

  return (
    <div className="space-y-4 pt-4 font-sans max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center px-2">
        <div className="flex items-center gap-4">
          <button onClick={() => onNavigate('compras')} className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-blue-600 shadow-sm transition-all">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-black text-slate-800 uppercase tracking-tighter leading-none">Detalle de Compra</h1>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Comprobante de Ingreso #{id.split('-')[0]}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Lado Izquierdo: Info y Tabla de Ítems */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Proveedor Emisor</label>
                <div className="flex items-center gap-2 mt-1">
                  <Truck size={14} className="text-blue-500" />
                  <p className="text-xs font-black text-slate-800 uppercase">{compra.proveedores?.name || 'Varios'}</p>
                </div>
              </div>
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Fecha de Registro</label>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar size={14} className="text-slate-400" />
                  <p className="text-xs font-bold text-slate-600">{new Date(compra.created_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="space-y-3 text-right md:text-left">
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Sucursal de Arribo</label>
                <div className="flex items-center gap-2 mt-1 md:justify-start justify-end">
                  <MapPin size={14} className="text-emerald-500" />
                  <p className="text-xs font-black text-slate-800 uppercase">{compra.sitios?.nombre}</p>
                </div>
              </div>
              <div>
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block">Estado de Operación</label>
                <span className="inline-block px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-600 text-[8px] font-black uppercase border border-emerald-100 mt-1">
                  Completada
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="bg-[#0B2A36] px-6 py-2.5 flex items-center gap-2">
              <Box size={14} className="text-blue-400" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest opacity-80">Renglones Recibidos</span>
            </div>
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-5 py-2 text-[8px] font-black text-slate-400 uppercase">Producto</th>
                  <th className="px-5 py-2 text-[8px] font-black text-slate-400 uppercase text-center">Cant.</th>
                  <th className="px-5 py-2 text-[8px] font-black text-slate-400 uppercase text-right">Costo U.</th>
                  <th className="px-5 py-2 text-[8px] font-black text-slate-400 uppercase text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-5 py-3">
                      <p className="text-[10px] font-black text-slate-700 uppercase leading-none">{item.products?.name}</p>
                    </td>
                    <td className="px-5 py-3 text-center text-xs font-bold text-slate-500">{item.quantity}</td>
                    <td className="px-5 py-3 text-right text-xs font-bold text-slate-500">$ {item.unit_cost.toLocaleString()}</td>
                    <td className="px-5 py-3 text-right text-xs font-black text-slate-800">$ {(item.quantity * item.unit_cost).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lado Derecho: Totales */}
        <div className="space-y-4">
          <div className="bg-[#0B2A36] rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center gap-2 mb-6 border-b border-white/5 pb-2">
              <Calculator size={14} className="text-blue-400" />
              <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Liquidación Final</span>
            </div>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Inversión Neta</span>
                <span className="text-3xl font-black text-white tracking-tighter">$ {compra.total_amount.toLocaleString()}</span>
              </div>
              <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20">
                <p className="text-[8px] font-bold text-blue-300 uppercase text-center">Mercadería ingresada correctamente en {compra.sitios?.nombre}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComprasDetalle;